import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Button, ElasticFormModal, Fetching, InfiniteScroll, ListFilter, ListRow, NoResults, Search, WithSelectable, WithTooltip } from 'basic-components';
import { Exit, Refresh } from 'basic-components/icons';
import { DataPrepQuery } from 'dashboard-services/api/dataPrep';
import Query from 'dashboard-services/api/Query';
import { CommonService } from 'dashboard-services/services';
import TimeService from 'dashboard-services/services/TimeService';
import { DropdownService } from 'primary-components';

import getApiConfig from 'api/ApiConfig';
import { sendEvent } from 'services/GAService';

import FilterCarousel from 'primary/FilterCarousel';

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';
import { When } from 'react-if';

import '../../Modal.scss';
import './Modal.scss';

const useSearchDataPreps = ({ getQueryBuilder, query }) =>
  useInfiniteQuery({
    ...DataPrepQuery.QUERIES.search(
      getQueryBuilder,
      {
        query,
        size: 50,
        latest: true
      }
    ),
    initialPageParam: 0,
    getNextPageParam: CommonService.getNextPageParam
  })
const wildcardQuery = query => `*${query}*`
const Modal = ({ optionalText = undefined, closeModal, onSubmit, defaultValue = "" }) => {
  const dispatch = useDispatch(),
        definedTime = useSelector(state => state.myAccountState.settingsState.definedTime),
        [value, setValue] = useState(defaultValue),
        [query, setQuery] = useState(""),
        [groupName, setGroupName] = useState(undefined),
        { favoriteDataPreps, storageScreen } = useSelector(state => state.myAccountState.settingsState),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        getQueryBuilder = useCallback(() =>
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
        , [getApiConfigLocal]),
        queryClient = useQueryClient(),
        [groupQuery, setGroupQuery] = useState(),
        { fetchNextPage: fetchMoreGroups, hasNextPage: hasNextPageGroups, isFetching: isFetchingGroups, isFetchingNextPage: isFetchingNextPageGroups, data: groups = [] } = DropdownService.useQueryGroups({
          getApiConfig: getApiConfigLocal,
          input: groupQuery,
          queryClient
        }),
        queryCalculated = useMemo(() => {
          const localQuery = []
          if(groupName !== "" && groupName !== undefined) {
            localQuery.push(`groupName.keyword=${groupName}`)
          }
          if(query) {
            localQuery.push(String(query).includes("=") ? query : "name=" + wildcardQuery(query))
          }
          return localQuery.length === 0 ? "*" : localQuery.join("&")
        }, [groupName, query]),
        { data: dataPrepsRaw, isPending, isFetchingNextPage, hasNextPage, fetchNextPage: onScroll, refetch, isRefetching } = useSearchDataPreps({ getQueryBuilder, query: queryCalculated }),
        dataPrepsFromApi = useMemo(() => (dataPrepsRaw?.pages || []).flatMap(d => d?.items || []), [dataPrepsRaw]),
        dataPreps = useMemo(() => !groupName && !query ? (favoriteDataPreps.map(d => Object.assign({}, d, { favorite: true }))).concat(dataPrepsFromApi) : dataPrepsFromApi, [dataPrepsFromApi, favoriteDataPreps, groupName, query]),

        onSubmitLocal = useCallback(() => {
          onSubmit(value, true)
          closeModal()
        }, [closeModal, onSubmit, value]),
        createNewDataPrep = useCallback(() => CommonService.openInNewTab(DashboardLinkFactory.dataStorage.newDataPrep({ storageScreen })), [storageScreen]),
        submitProps = useMemo(() => ({
          disabled: value === undefined || value === "" || value === " ",
          noDebounce: true
        }), [value]),
        formProps = useMemo(() => ({
          noDebounce: true
        }), []),
        getUrl = useCallback(dataprep => DashboardLinkFactory.dataStorage.dataPrep({ groupName: dataprep.groupName, name: dataprep.name, storageScreen }), [storageScreen]),
        preventFilterSubmit = useCallback(event => event.preventDefault(), []),
        scrollContRef = useRef()

  useEffect(() => {
    const event = {
      category: "view_item",
      action: "dataprep_picker",
      label: "picker",
    };
    dispatch(sendEvent(event))
  }, [dispatch])

  return (
    <ElasticFormModal
        className="ng-common-picker__modal ng-dp-picker__modal"
        formProps={formProps}
        onClose={closeModal}
        onSubmit={onSubmitLocal}
        optionalText={optionalText}
        submitProps={submitProps}
        submitText="Select"
        title={(
          <div className="ng-common-picker__modal__title">
            <p className="ng-common-picker__modal__title__left">Select or </p>
            <Button
                onClick={createNewDataPrep}
                size="large"
                variant="text"
            >
              Create
            </Button>
            <p className="ng-common-picker__modal__title__right">Data prep</p>
          </div>
        )}
    >  
      <div className="ng-file-picker__modal__top">
        <div className="ng-file-picker__modal__top__search">
          <Search
              data-testid="data-prep-search"
              onSearch={setQuery}
              size="large"
              variant="outline"
          />
        </div>
        <FilterCarousel
            className="ng-file-picker__modal__filters"
            onClickCapture={preventFilterSubmit}
        >
          <ListFilter
              defaultValue={groupName}
              fetchNextPage={fetchMoreGroups}
              hasNextPage={hasNextPageGroups}
              isFetching={isFetchingGroups || isFetchingNextPageGroups}
              label="Group"
              name="groupName"
              onChange={setGroupName}
              onInputChange={setGroupQuery}
              options={groups}
          />
        </FilterCarousel>
      </div>
      <div className="ng-common-picker__modal__headers">
        <div className="ng-common-picker__modal__headers__cell">
          Name
        </div>
        <div className="ng-common-picker__modal__headers__cell">
          Group
        </div>
        <div className="ng-common-picker__modal__headers__cell">
          Last modified
        </div>
        <WithTooltip
            data-place="left"
            short
            text={isRefetching || isPending || isFetchingNextPage ? "Data is refreshing" : "Refresh data"}
        >
          <div
              className="ng-common-picker__modal__headers__cell"
              onClick={isRefetching || isPending || isFetchingNextPage ? undefined : refetch}
          >
            {isRefetching || isPending || isFetchingNextPage ? (
              <Fetching
                  loading={isFetchingNextPage || isPending || isRefetching}
                  size={12}
              />
            ) : (
              <Refresh
                  color="blue-bright"
                  height={16}
                  width={16}
              />
            )}
          </div>
        </WithTooltip>
      </div>
      <div 
          className="ng-common-picker__modal__content"
          ref={scrollContRef}
      >
        <InfiniteScroll
            className="ng-common-picker__modal__content__scroll"
            hasMore={hasNextPage}
            initialLoad={false}
            loadMore={onScroll}
            threshold={800}
            useWindow={false}
        >
          {dataPreps.map((dp, index) => (
            <WithSelectable
                index={index}
                key={`DataPrep${index}`}
                selected={value?.name === dp.name && dp.id === value?.id && dp.groupName === value.groupName}
                setSelected={() => setValue(dp)}
            >
              <ListRow
                  className="ng-common-picker__modal__content__scroll__row"
                  contentProps={{
                    onDoubleClick: () => {
                      setValue(dp)
                      onSubmitLocal()
                      closeModal()
                    }
                  }}
                  onClick={() => setValue(dp)}
                  scrollContRef={scrollContRef.current}
              >
                <div className="ng-common-picker__modal__content__scroll__row__cell">
                  {dp.name}
                </div>
                <div className="ng-common-picker__modal__content__scroll__row__cell">
                  {dp.groupName}
                </div>
                <div className="ng-common-picker__modal__content__scroll__row__cell">
                  {dp.modifyDate ? TimeService.getUserFormattedDate(dp.modifyDate, definedTime) : ""}
                </div>
                <div className="ng-common-picker__modal__exit">
                  <WithTooltip
                      data-place="top"
                      short
                      text="Open data prep"
                  >
                    <div onClick={() => CommonService.openInNewTab(getUrl(dp))}>
                      <Exit
                          color="blue-bright"
                          height={16}
                          width={16}
                      />
                    </div>
                  </WithTooltip>
                </div>
              </ListRow>
            </WithSelectable>
          ))}
        </InfiniteScroll>
        <When condition={dataPreps.length === 0 && !isPending && !isRefetching}>
          {() => (
            <NoResults
                isModal
            />
          )}
        </When>
      </div>
    </ElasticFormModal>
  )
}

Modal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  defaultValue: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  optionalText: PropTypes.string
}

export default memo(Modal);
